import { Component } from "react";
import { Button } from "@/components/ui/button";
import { AlertTriangle, RefreshCw, LogOut } from "lucide-react";
import { reportBoundaryError } from "@/lib/globalErrorHandler";

// Error boundary de topo — captura crashes de render/lifecycle em qualquer descendente
// e mostra fallback amigável em vez de tela branca (bug relatado fev/2026 no mobile).
// Reset via "Tentar novamente" força um remount limpo; se persistir, oferece Sair pra
// invalidar sessão e voltar ao login.
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorId: 0 };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    // Log local pro DevTools + envio remoto pra /api/mobile-logs (visível no painel SA)
    console.error("[ErrorBoundary] Uncaught error:", error, info?.componentStack);
    reportBoundaryError(error, info?.componentStack);
  }
  handleRetry = () => {
    this.setState((s) => ({ hasError: false, error: null, errorId: s.errorId + 1 }));
  };
  handleLogout = () => {
    // Limpa sessão localmente + força reload pro login (bypass qualquer estado corrompido)
    try { localStorage.clear(); } catch { /* SSR guard */ }
    window.location.assign("/login");
  };
  render() {
    if (this.state.hasError) {
      const msg = this.state.error?.message || "Erro inesperado";
      return (
        <div className="min-h-screen flex items-center justify-center px-4 bg-orange-50/50" data-testid="error-boundary-screen">
          <div className="max-w-md w-full bg-white rounded-lg border border-orange-200 shadow-md p-6 sm:p-8 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-red-100 flex items-center justify-center mb-4">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h1 className="text-xl font-bold text-slate-800 mb-2">Algo deu errado</h1>
            <p className="text-sm text-slate-600 mb-2">A tela travou por um erro inesperado.</p>
            <p className="text-xs text-slate-400 mb-6 font-mono break-all">{msg}</p>
            <div className="flex gap-2">
              <Button onClick={this.handleRetry} className="flex-1 bg-orange-600 hover:bg-orange-700" data-testid="error-boundary-retry">
                <RefreshCw className="w-4 h-4 mr-2" /> Tentar novamente
              </Button>
              <Button onClick={this.handleLogout} variant="outline" data-testid="error-boundary-logout">
                <LogOut className="w-4 h-4 mr-2" /> Sair
              </Button>
            </div>
          </div>
        </div>
      );
    }
    // errorId key força remount inteiro do subtree ao clicar em "Tentar novamente"
    return <div key={this.state.errorId}>{this.props.children}</div>;
  }
}
